"use client";
import { Button } from "@gigpro/ui/Button";
import { Close, Upload } from "@gigpro/ui/Icons";
import { useRef } from "react";

interface InputFileProps extends Omit<React.ComponentProps<"div">, "onChange"> {
  value?: Array<File>;
  error?: Array<{
    message: string;
    filename: string;
  }>;
  loading?: boolean;
  accept?: string;
  succeedFiles?: Array<File>;
  onChange: (files: Array<File>) => void;
}

export const InputFile = ({
  onChange,
  loading,
  value = [],
  error,
  accept,
  succeedFiles,
  ...rest
}: Readonly<InputFileProps>) => {
  const ref = useRef<HTMLInputElement>(null);
  const onRemove = (idx: number) => {
    onChange?.(value?.filter((_, i) => i !== idx));
  };

  const getError = (file: File) => {
    return error?.find((e) => e.filename === file.name)?.message;
  };
  const isSucceed = (file: File) => {
    return succeedFiles?.some((f) => f.name === file.name);
  };

  return (
    <div {...rest}>
      <span className="flex items-center gap-2 py-2">
        <Button
          variant="contained"
          size="small"
          className="shrink-0"
          onClick={() => ref?.current?.click()}
          disabled={loading}
        >
          <Upload className="mr-1 size-3.5" />
          Attach a file
        </Button>
        <input
          className="hidden"
          ref={ref}
          multiple
          type="file"
          value=""
          accept={accept}
          onChange={(e) => onChange(Array.from(e.target.files || []))}
        />
        {accept && <p className="tracking-tight text-gray">{accept} are supported</p>}
      </span>
      {value.map((file, idx) => (
        <div
          key={file.name}
          className="flex items-center gap-1 font-semibold text-brand [&[data-error=true]]:text-error [&[data-succeed=true]]:text-success"
          data-loading={loading}
          data-error={!!getError(file)}
          data-succeed={!!isSucceed(file)}
        >
          <span className="text-small font-semibold underline">{file.name}</span>
          <Button
            variant="text"
            icon
            data-error={!!getError(file)}
            data-succeed={!!isSucceed(file)}
            className="bg-transparent [&[data-error=true]]:text-error [&[data-succeed=true]]:text-success"
            loading={loading}
            onClick={() => onRemove(idx)}
          >
            <Close />
          </Button>
          {getError(file) && <p className="text-error">({getError(file)})</p>}
        </div>
      ))}
    </div>
  );
};

InputFile.displayName = "InputFile";
